import { forwardRef, useState } from "react";
import { Box, CardMedia, CardMediaProps } from "@mui/material";
import {
  IMAGE_FADE_IN_MS,
  ImageLoadingPlaceholder,
} from "../ImageLoadingPlaceholder";

/**
 * 카드 썸네일 이미지 영역 props — fallback default 이미지 자동 처리.
 *
 * `useDefaultImage` 가 true 이면 요청한 그림이 그려지기 전과 로딩 실패 시 크기별 기본 SVG 를 대신 그린다.
 * 손가락 커서는 `onClick` 을 넘길 때만 띄운다 — 넘기지 않으면 cursor 를 선언하지 않아, 클릭을 가진
 * 조상(감싼 버튼 등)의 커서를 그대로 따른다.
 */
export interface CardImageProps
  extends Omit<CardMediaProps, "image" | "onLoad" | "onError"> {
  alt?: string;
  image?: string;
  disabled?: boolean;
  size?: "small" | "large";
  /** 요청한 그림 전후로 기본 SVG 를 거칠지 (기본 true) — 끄면 처음부터 `image` 를 그린다 */
  useDefaultImage?: boolean;
  /** 화면에 들어올 때 받을지(`lazy`) — 기본은 즉시 로드. 카드가 많은 목록만 넘긴다 */
  loading?: "lazy" | "eager";
  /** 같은 URL 을 캔버스에 그리는 소비자가 있으면 CORS 모드를 맞춘다 */
  crossOrigin?: "anonymous" | "use-credentials";
}

/**
 * 브라우저는 같은 `src` 에 `onLoad` 를 한 번만 보낸다 — placeholder 를 거치지 않는 카드는
 * 로드 완료가 한 번뿐이라, 로딩 종료를 스왑이 일어났는지로 판정할 수 없다.
 */
const useCardImageDisplay = (
  image: string | undefined,
  defaultImage: string | null
) => {
  const [hasSwapped, setHasSwapped] = useState(false);
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const [loadedImage, setLoadedImage] = useState<string | null>(null);

  const isImageFailed = !!image && failedImage === image;
  const isSwapSettled = !defaultImage || hasSwapped;
  const showsImage = !!image && isSwapSettled && !isImageFailed;
  const displayImage = showsImage ? image : defaultImage ?? image;
  /** 요청한 그림이 실제로 그려졌는지 — placeholder 가 떠 있는 것과 구분한다 */
  const isImageRendered = !!image && loadedImage === image;

  // 두 핸들러의 나머지 갈래는 요청한 그림의 로드 결과가 아니다 — 기본 SVG 단계를 지났다는
  // 사실만 남긴다. 아직 주소를 못 받은 카드(`image` 미도착)도 여기로 온다.
  const handleRenderedImageLoad = () => {
    if (image && showsImage) {
      setLoadedImage(image);
      return;
    }
    setHasSwapped(true);
  };

  const handleRenderedImageError = () => {
    if (image && showsImage) {
      setFailedImage(image);
      return;
    }
    setHasSwapped(true);
  };

  return {
    displayImage,
    isLoading: !!image && !isImageFailed && !isImageRendered,
    handleRenderedImageLoad,
    handleRenderedImageError,
  };
};

export const CardImage = forwardRef<HTMLImageElement, CardImageProps>(
  (
    {
      alt = "card image",
      size = "small",
      disabled = false,
      image,
      useDefaultImage = true,
      onClick,
      sx,
      ...props
    },
    ref
  ) => {
    const defaultImage = `/icons/card-image/${size}-${
      disabled ? "disable" : "enable"
    }-card-image.svg`;

    const {
      displayImage,
      isLoading,
      handleRenderedImageLoad,
      handleRenderedImageError,
    } = useCardImageDisplay(image, useDefaultImage ? defaultImage : null);

    return (
      <Box sx={frame}>
        <CardMedia
          ref={ref}
          alt={alt}
          image={displayImage}
          component="img"
          onClick={onClick}
          onLoad={handleRenderedImageLoad}
          onError={handleRenderedImageError}
          {...props}
          sx={[
            {
              cursor: onClick ? "pointer" : undefined,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              // 로딩 표시가 덮고 있는 동안에는 감췄다가 사진이 그려지면 서서히 드러난다
              opacity: isLoading ? 0 : 1,
              transition: `opacity ${IMAGE_FADE_IN_MS}ms ease`,
            },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
        />
        {isLoading && <ImageLoadingPlaceholder />}
      </Box>
    );
  }
);

CardImage.displayName = "CardImage";

/**
 * 로딩 표시를 이미지 위에 겹치기 위한 기준 상자 — 크기는 그대로 이미지가 정한다.
 * 정렬을 물려주지 않으면 자기 높이를 정하는 이미지(`height: auto`)가 좌상단에 붙는다.
 */
const frame = {
  position: "relative",
  width: "100%",
  height: "100%",
  minHeight: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
} as const;
